import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { track } from '@/lib/analytics';
import { setOnboardingStepDone } from '@/lib/storage';
import { OnboardingProgress } from '@/components/OnboardingProgress';
import { Button } from '@/components';
import {
  FiDollarSign,
  FiCalendar,
  FiFileText,
  FiMessageCircle,
} from 'react-icons/fi';

const TOTAL_STEPS = 11;

const PAIN_POINT_OPTIONS = [
  { id: 'money', label: 'Disagreements about money', icon: FiDollarSign },
  { id: 'scheduling', label: 'Scheduling conflicts', icon: FiCalendar },
  { id: 'documentation', label: 'Lack of documentation', icon: FiFileText },
  {
    id: 'communication',
    label: 'Communication breakdowns',
    icon: FiMessageCircle,
  },
];

function PainPointPage() {
  const navigate = useNavigate();
  const [selectedPainPoint, setSelectedPainPoint] = useState<string | null>(
    null
  );

  useEffect(() => {
    track('Pain Point Screen');
  }, []);

  const handleContinue = () => {
    if (selectedPainPoint) {
      track('Pain Point Submitted', { pain_point: selectedPainPoint });
    }
    setOnboardingStepDone('pain_point');
    navigate({ to: '/app/onboarding/profile' });
  };

  const handleSkip = () => {
    track('Pain Point Skipped');
    setOnboardingStepDone('pain_point');
    navigate({ to: '/app/onboarding/profile' });
  };

  return (
    <div className='min-h-screen bg-sky-light flex flex-col'>
      <OnboardingProgress currentStep={7} totalSteps={TOTAL_STEPS} />
      <div className='flex-1 flex flex-col justify-between px-6 max-w-lg mx-auto w-full'>
        <div className='flex-1 pt-5'>
          <h1
            className='text-[28px] font-extrabold text-gray-900 text-center'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            What's your biggest frustration right now?
          </h1>
          <p
            className='mt-2.5 text-base text-gray-500 text-center leading-relaxed mb-6'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            We'll focus on solving this first.
          </p>

          <div className='space-y-2.5'>
            {PAIN_POINT_OPTIONS.map((option) => {
              const isSelected = selectedPainPoint === option.id;
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => setSelectedPainPoint(option.id)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-colors text-left ${
                    isSelected
                      ? 'border-primary bg-primary/[0.04]'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-[10px] flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-primary' : 'bg-primary/[0.08]'
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        isSelected ? 'text-white' : 'text-primary'
                      }`}
                    />
                  </div>
                  <span
                    className={`flex-1 text-base ${
                      isSelected
                        ? 'text-gray-900 font-semibold'
                        : 'text-gray-700'
                    }`}
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    }}
                  >
                    {option.label}
                  </span>
                  <div
                    className={`w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary' : 'border-gray-200'
                    }`}
                  >
                    {isSelected && (
                      <div className='w-3 h-3 rounded-full bg-primary' />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className='w-full pb-6 pt-4 space-y-2.5'>
          <Button
            fullWidth
            onClick={handleContinue}
            disabled={!selectedPainPoint}
          >
            Continue
          </Button>
          <button
            onClick={handleSkip}
            className='w-full py-3 text-center text-gray-500 font-semibold hover:text-gray-700 transition-colors'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/onboarding/pain-point')({
  component: PainPointPage,
});
